/*
 * Quiz → cart handoff (bounce page). Runs on the Shopify storefront, same-origin
 * with the cart. Resolves the quiz's UUID + tier into a pre-built kit payload,
 * adds it, applies the BUNDLE discount, then redirects to checkout/cart.
 *
 * Contract: GET {quizOrigin}/api/quiz/ai/cart-handoff/{token}?tier={full|trial}
 *   -> { payload: { items: [...] }, discount: "BUNDLE30" | "BUNDLE20", tier }
 * Mirrors the live kit-builder flow (kit-builder.js): /cart/add.js then
 * /cart/update.js. No PII crosses the wire — only the opaque token.
 */
(function () {
  var root = document.getElementById('quiz-cart-handoff');
  if (!root) return;

  var quizOrigin = (root.dataset.quizOrigin || '').replace(/\/+$/, '');
  var target = root.dataset.checkoutTarget === 'cart' ? '/cart' : '/checkout';

  function show(state) {
    root.querySelectorAll('[data-kch-state]').forEach(function (el) {
      el.hidden = el.getAttribute('data-kch-state') !== state;
    });
  }

  function fail(reason) {
    show('error');
    if (window.console) console.error('[quiz-cart-handoff]', reason);
  }

  var params = new URLSearchParams(window.location.search);
  var token = params.get('token');
  var tier = params.get('tier') === 'trial' ? 'trial' : 'full';

  // Cross-domain attribution (tracking-implementation.md §5): the quiz appends
  // the landing query string (fbclid, utm_*, …) onto this bounce URL alongside
  // token+tier. Carry everything EXCEPT the handoff-control params onto the
  // final checkout/cart redirect, so Meta keeps the click id across the
  // quiz→Shopify domain hop. Without this the hop drops attribution.
  function redirectTarget() {
    var carried = new URLSearchParams(window.location.search);
    carried.delete('token');
    carried.delete('tier');
    var qs = carried.toString();
    return qs ? target + (target.indexOf('?') >= 0 ? '&' : '?') + qs : target;
  }

  if (!token) return fail('missing token');
  if (!quizOrigin) return fail('quiz origin not configured on the section');

  var handoffUrl =
    quizOrigin + '/api/quiz/ai/cart-handoff/' + encodeURIComponent(token) + '?tier=' + encodeURIComponent(tier);

  fetch(handoffUrl, { headers: { Accept: 'application/json' } })
    .then(function (r) {
      if (!r.ok) throw new Error('handoff ' + r.status);
      return r.json();
    })
    .then(function (data) {
      if (!data || !data.payload) throw new Error('empty payload');

      // Guard against a kit line with a missing/zero variant id BEFORE posting:
      // Shopify's /cart/add.js rejects such a payload with an opaque 422
      // "Cannot find variant" (the exact failure reported on this page). The
      // parent line is the only env-driven id and ships blank in .env.example,
      // so a stale payload can carry id 0. Surface which _kit_role is bad in the
      // console instead of firing a POST that is guaranteed to fail.
      var items = (data.payload && data.payload.items) || [];
      var invalid = items.filter(function (it) { return !it || !(parseInt(it.id, 10) > 0); });
      if (!items.length || invalid.length) {
        var offending = invalid.map(function (it) {
          return ((it && it.properties && it.properties._kit_role) || '?') + ':' + ((it && it.id) || 0);
        }).join(', ');
        throw new Error('invalid cart payload — missing variant id [' + (offending || 'no items') + ']');
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data.payload)
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (e) {
              throw new Error(e.description || e.message || 'add.js failed');
            });
          }
          return data;
        });
    })
    .then(function (data) {
      // Apply the bundle code; never block the redirect if it fails (checkout
      // accepts the code manually), mirroring kit-builder.js.
      if (!data.discount) return;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: data.discount })
      }).catch(function () {});
    })
    .then(function () {
      window.location.href = redirectTarget();
    })
    .catch(fail);
})();
